import { initializeApp, FirebaseApp, getApps, getApp } from "firebase/app";
import { getAuth, Auth, connectAuthEmulator } from "firebase/auth";
import {
  getFirestore,
  Firestore,
  connectFirestoreEmulator,
} from "firebase/firestore";
import {
  getFunctions,
  Functions,
  connectFunctionsEmulator,
} from "firebase/functions";
import { getStorage, FirebaseStorage } from "firebase/storage";

/**
 * Firebase Configuration Interface
 */
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

/**
 * Load Firebase Config from Environment Variables
 */
const firebaseConfig: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "",
};

/**
 * Validate Firebase Config
 */
const validateConfig = (): void => {
  const requiredKeys: (keyof FirebaseConfig)[] = [
    "apiKey",
    "authDomain",
    "projectId",
    "storageBucket",
    "messagingSenderId",
    "appId",
  ];

  const missingKeys = requiredKeys.filter((key) => !firebaseConfig[key]);

  if (missingKeys.length > 0) {
    throw new Error(
      `Missing Firebase configuration: ${missingKeys.join(", ")}. ` +
        "Please check your .env file and ensure all VITE_FIREBASE_* variables are set.",
    );
  }
};

// Validate config before initializing
validateConfig();

/**
 * Initialize Firebase App
 */
export const app: FirebaseApp = getApps().length
  ? getApp()
  : initializeApp(firebaseConfig);

/**
 * Initialize Firebase Services
 */
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const functions: Functions = getFunctions(app, "europe-west1"); // Adjust region as needed
export const storage: FirebaseStorage = getStorage(app);

/**
 * Connect to Emulators (Development Only)
 */
const useEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true";

if (useEmulator && import.meta.env.DEV) {
  console.warn("🔧 Firebase Emulator Mode Enabled");

  const firestoreHost =
    import.meta.env.VITE_FIRESTORE_EMULATOR_HOST || "localhost";
  const firestorePort = parseInt(
    import.meta.env.VITE_FIRESTORE_EMULATOR_PORT || "8080",
  );
  const authHost = import.meta.env.VITE_AUTH_EMULATOR_HOST || "localhost";
  const authPort = parseInt(import.meta.env.VITE_AUTH_EMULATOR_PORT || "9099");

  connectFirestoreEmulator(db, firestoreHost, firestorePort);
  connectAuthEmulator(auth, `http://${authHost}:${authPort}`, {
    disableWarnings: true,
  });
  connectFunctionsEmulator(functions, "localhost", 5001);
}

/**
 * Export config for reference
 */
export { firebaseConfig };
